import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  Fade,
  useTheme,
  alpha,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  PersonAdd,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Warning,
  AdminPanelSettings,
  Person,
  MoreVert,
  Refresh,
  Download,
  Upload,
  Visibility,
  Email,
  LocationOn,
  TrendingUp,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';

interface User {
  username: string;
  role: string; // Changed to string to match AdminUser
  status: 'active' | 'inactive' | 'suspended';
  created_at?: string;
  last_activity?: string;
  total_uploads: number;
  total_analyses: number;
}

const UserManagementPage: React.FC = () => {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Fetch users data
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => apiService.getAllUsersAdmin(),
    enabled: isAdmin,
  });

  // Memoize users array to prevent infinite re-renders
  const users = useMemo(() => usersData?.users || [], [usersData?.users]);

  // Mutations for user management
  const updateRoleMutation = useMutation({
    mutationFn: ({ username, role }: { username: string; role: string }) =>
      apiService.updateUserRole(username, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      handleActionMenuClose();
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (username: string) => apiService.deleteUserAdmin(username),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      handleActionMenuClose();
    },
  });

  useEffect(() => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Access denied. This page is only available to administrators.
      </Alert>
    );
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUserId(userId);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedUserId(null);
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
    handleActionMenuClose();
  };

  const handleEditUser = (username: string) => {
    // Implementation for editing user
    console.log('Edit user:', username);
    handleActionMenuClose();
  };

  const handleToggleRole = (username: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (window.confirm(`Are you sure you want to change ${username}'s role to ${newRole}?`)) {
      updateRoleMutation.mutate({ username, role: newRole });
    }
    handleActionMenuClose();
  };

  const handleDeleteUser = (username: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      deleteUserMutation.mutate(username);
    }
    handleActionMenuClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'inactive': return <Warning />;
      case 'suspended': return <Block />;
      default: return <Person />;
    }
  };

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? <AdminPanelSettings /> : <Person />;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Box>
        {/* Header Section */}
        <Fade in={true} timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  👥 User Management
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Manage user accounts, permissions, and system access
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.2),
                        transform: 'rotate(180deg)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                >
                  Add User
                </Button>
              </Stack>
            </Box>
          </Box>
        </Fade>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Card
                elevation={0}
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                    <Person sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                      {usersLoading ? '...' : users.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Card
                elevation={0}
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.2)}`,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.success.main, width: 56, height: 56 }}>
                    <CheckCircle sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.success.main }}>
                      {usersLoading ? '...' : users.filter(u => u.status === 'active').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Users
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Card
                elevation={0}
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.2)}`,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 56, height: 56 }}>
                    <AdminPanelSettings sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.secondary.main }}>
                      {usersLoading ? '...' : users.filter(u => u.role === 'admin').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Administrators
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              whileHover={{ scale: 1.02, y: -4 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Card
                elevation={0}
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: `0 12px 32px ${alpha(theme.palette.info.main, 0.2)}`,
                  },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.info.main, width: 56, height: 56 }}>
                    <TrendingUp sx={{ fontSize: '1.5rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.info.main }}>
                      {usersLoading ? '...' : users.reduce((sum, u) => sum + (u.total_uploads || 0), 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Uploads
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Role Filter</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role Filter"
                  onChange={(e) => setRoleFilter(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="user">Users</MenuItem>
                  <MenuItem value="admin">Administrators</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Export Users">
                  <IconButton
                    sx={{
                      background: alpha(theme.palette.info.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.info.main, 0.2),
                      },
                    }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Import Users">
                  <IconButton
                    sx={{
                      background: alpha(theme.palette.success.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.success.main, 0.2),
                      },
                    }}
                  >
                    <Upload />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Enhanced Users Table */}
        <Card
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 3,
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            },
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Activity</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Joined</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {filteredUsers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user, index) => (
                      <TableRow
                        key={user.username}
                        component={motion.tr}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        sx={{
                          '&:hover': {
                            background: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              sx={{
                                bgcolor: user.role === 'admin'
                                  ? theme.palette.secondary.main
                                  : theme.palette.primary.main,
                                width: 40,
                                height: 40,
                                fontSize: '0.9rem',
                                fontWeight: 600,
                              }}
                            >
                              {user.username.substring(0, 2).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {user.username}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {user.username}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Chip
                            icon={getRoleIcon(user.role)}
                            label={user.role === 'admin' ? 'Administrator' : 'User'}
                            color={user.role === 'admin' ? 'secondary' : 'primary'}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            icon={getStatusIcon(user.status)}
                            label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                            color={getStatusColor(user.status) as any}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {user.total_uploads || 0} uploads
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.total_analyses || 0} analyses
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            Not specified
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last: {user.last_activity ? new Date(user.last_activity).toLocaleDateString() : 'Never'}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <IconButton
                            onClick={(e) => handleActionMenuOpen(e, user.username)}
                            sx={{
                              background: alpha(theme.palette.action.hover, 0.05),
                              '&:hover': {
                                background: alpha(theme.palette.action.hover, 0.1),
                              },
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: alpha(theme.palette.background.default, 0.5),
            }}
          />
        </Card>

        {/* Action Menu */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleActionMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            },
          }}
        >
          <MenuItem onClick={() => {
            const user = users.find(u => u.username === selectedUserId);
            if (user) handleViewUser(user);
          }}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => selectedUserId && handleEditUser(selectedUserId)}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => {
            const user = users.find(u => u.username === selectedUserId);
            if (user) handleToggleRole(user.username, user.role);
          }}>
            <ListItemIcon>
              <AdminPanelSettings fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {users.find(u => u.username === selectedUserId)?.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
            </ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              const user = users.find(u => u.username === selectedUserId);
              if (user) handleDeleteUser(user.username);
            }}
            sx={{ color: theme.palette.error.main }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem>
        </Menu>

        {/* User Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: selectedUser?.role === 'admin'
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                  width: 56,
                  height: 56,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                }}
              >
                {selectedUser?.username.substring(0, 2).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {selectedUser?.username}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    icon={getRoleIcon(selectedUser?.role || 'user')}
                    label={selectedUser?.role === 'admin' ? 'Administrator' : 'User'}
                    color={selectedUser?.role === 'admin' ? 'secondary' : 'primary'}
                    size="small"
                  />
                  <Chip
                    icon={getStatusIcon(selectedUser?.status || 'active')}
                    label={(selectedUser?.status || 'active').charAt(0).toUpperCase() + (selectedUser?.status || 'active').slice(1)}
                    color={getStatusColor(selectedUser?.status || 'active') as any}
                    size="small"
                  />
                </Stack>
              </Box>
            </Stack>
          </DialogTitle>

          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    background: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                    📧 Contact Information
                  </Typography>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Email color="action" />
                      <Typography variant="body2">
                        Not provided
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <LocationOn color="action" />
                      <Typography variant="body2">
                        Not specified
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    background: alpha(theme.palette.success.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                    📊 Activity Statistics
                  </Typography>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Total Uploads:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser?.total_uploads || 0}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Total Analyses:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser?.total_analyses || 0}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    background: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                    📅 Account Timeline
                  </Typography>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Account Created:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser?.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : 'Unknown'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Last Activity:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser?.last_activity ? new Date(selectedUser.last_activity).toLocaleDateString() : 'Never'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setDialogOpen(false)}>
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                setDialogOpen(false);
                if (selectedUser) handleEditUser(selectedUser.username);
              }}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              }}
            >
              Edit User
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default UserManagementPage;
